import { DataTableDataSearchDto } from '@asset-metrix/portal-gateway-client-js';
import type { StateCreator } from 'zustand';

import { PermanentError } from '../../utils/errors';
import type { RemoveFunctions } from '../../utils/types';
import type { StateSlices } from '..';
import { pickSelectionsFromFilter } from '../hooks/pickSelectionFromFilter';

import type { Filter, FilterData } from './filterTypes';

export type FilterSlice = {
  filterData: Partial<{
    [entityId: string]: {
      /**
       * visualisedFilter contains the data that is used for the visualisation towards the user
       * this needs to be updated whenever there is user input, or external factors (i.e. responses from requests) change the data/selections that should be shown to a user
       */
      visualisedFilter: Filter;
      /**
       * selectionForQueryKey contains the selection info that is used to fetch filter info
       * this needs to be updated whenever there is user input, BUT NOT when filter requests resolve
       */
      selectionForQueryKey?: DataTableDataSearchDto['selection'];
      /**
       * Initialised is set after filters have been completely pre-set from the url,
       * this always happens, even if the url contains no filters.
       * When initialised is true, loading of filter data begins and once it
       * completes, loaded is also set to true. Filter meta for a page will also be
       * set to loaded if no filters are configured - the page loaders depend on this.
       */
      meta: { loaded: boolean; initialised: boolean; error?: string };
    };
  }>;
  isPageFilterLoading: boolean;
  setPageFilterLoading: (isLoading: boolean) => void;
  updateFilterData: (entityId: string, data: Filter) => void;
  setSingleFilterData: (
    entityId: string,
    columnKey: string,
    data: FilterData
  ) => void;
  setFilterError: (entityId: string, error: string) => void;
  setFilterInitialised: (entityId: string, initialised?: boolean) => void;
  setFilterLoaded: (entityId: string, loaded?: boolean) => void;
  presetFilterSelection: <
    D extends FilterData['display'],
    T extends FilterData & { display: D },
  >(
    entityId: string,
    key: string,
    displayType: D,
    type: T['type'],
    selection?: T['selection']
  ) => void;
  setFilterSelection: <
    D extends FilterData['display'],
    T extends FilterData & { display: D },
  >(
    entityId: string,
    key: string,
    type: T['type'],
    displayType: D,
    selection?: T['selection']
  ) => void;
  cloneFilter: (entityIdFrom: string, entityIdTo: string) => void;
  deleteFilter: (entityId: string) => void;
  resetFilterSelection: (
    entityId: string,
    key?: string,
    omitFilterKeys?: string[]
  ) => void;
  setAllFilterUnLoaded: () => void;
  resetFilterSlice: () => void;
};

const initialState: RemoveFunctions<FilterSlice> = {
  filterData: {},
  isPageFilterLoading: false,
};

const initialFilterData: FilterSlice['filterData'][string] = {
  meta: { initialised: false, loaded: false },
  visualisedFilter: {},
};

export const createFilterSlice: StateCreator<
  StateSlices,
  [],
  [],
  FilterSlice
> = (set) => {
  return {
    ...initialState,
    setPageFilterLoading: (isPageFilterLoading) =>
      set((state) => ({ ...state, isPageFilterLoading })),
    updateFilterData: (entityId: string, data: Filter) => {
      set((state) => {
        const filterData = structuredClone(state.filterData);

        filterData[entityId] =
          filterData[entityId] ?? structuredClone(initialFilterData);

        filterData[entityId].visualisedFilter = data;
        filterData[entityId].meta = { initialised: true, loaded: true };

        return { filterData };
      });
    },
    setSingleFilterData: (entityId, columnKey, data) => {
      set((state) => {
        const filterData = structuredClone(state.filterData);

        filterData[entityId] =
          filterData[entityId] ?? structuredClone(initialFilterData);

        filterData[entityId].visualisedFilter[columnKey] = data;
        filterData[entityId].meta = { initialised: true, loaded: true };

        return { filterData };
      });
    },
    setFilterError: (entityId: string, error: string) => {
      set((state) => {
        const filterData = structuredClone(state.filterData);

        filterData[entityId] =
          filterData[entityId] ?? structuredClone(initialFilterData);

        filterData[entityId].meta.error = error;
        filterData[entityId].meta.loaded = false;

        return { filterData };
      });
    },
    setFilterInitialised: (entityId: string, initialised = true) => {
      set((state) => {
        const filterData = structuredClone(state.filterData);

        filterData[entityId] =
          filterData[entityId] ?? structuredClone(initialFilterData);

        filterData[entityId].meta = { initialised, loaded: false };

        return { filterData };
      });
    },
    setFilterLoaded: (entityId: string, loaded = true) => {
      set((state) => {
        const filterData = structuredClone(state.filterData);

        filterData[entityId] =
          filterData[entityId] ?? structuredClone(initialFilterData);

        filterData[entityId].meta = {
          initialised: true,
          loaded:
            Object.keys(state.filterData[entityId]?.visualisedFilter ?? {})
              .length > 0 && !loaded
              ? false
              : true,
        };

        return { filterData };
      });
    },
    presetFilterSelection: <
      D extends FilterData['display'],
      T extends FilterData & { display: D },
    >(
      entityId: string,
      key: string,
      displayType: D,
      type: T['type'],
      selection: T['selection']
    ) => {
      set((state) => {
        const newData = {
          data: [],
          display: displayType,
          selection,
          type,
        } as FilterData;

        const filterData = structuredClone(state.filterData);

        filterData[entityId] =
          filterData[entityId] ?? structuredClone(initialFilterData);

        filterData[entityId].visualisedFilter[key] = newData;
        filterData[entityId].meta = { initialised: false, loaded: false };
        filterData[entityId].selectionForQueryKey = pickSelectionsFromFilter(
          filterData[entityId].visualisedFilter
        );

        return { filterData };
      });
    },
    setFilterSelection: <
      D extends FilterData['display'],
      T extends FilterData & { display: D },
    >(
      entityId: string,
      key: string,
      type: T['type'],
      displayType: D,
      selection?: T['selection']
    ) => {
      function isT(filterData?: FilterData): filterData is T {
        return filterData?.display === displayType;
      }

      set((state) => {
        const filterData = structuredClone(state.filterData);

        filterData[entityId] =
          filterData[entityId] ??
          // allow setFilterSelection to init the filter if not yet initialised
          structuredClone({
            ...initialFilterData,
            visualisedFilter: {
              [key]: { display: displayType, data: [], type },
            } as Filter,
          });

        if (isT(filterData[entityId]?.visualisedFilter[key])) {
          filterData[entityId].visualisedFilter[key].selection = selection;
          filterData[entityId].meta = { initialised: true, loaded: false };
          filterData[entityId].selectionForQueryKey = pickSelectionsFromFilter(
            filterData[entityId].visualisedFilter
          );
          return { filterData };
        }
        throw new PermanentError(
          `Cannot set selection for filter with key ${key}, as display type does not match (expected: ${displayType}, actual: ${filterData[entityId]?.visualisedFilter[key]?.display})`
        );
      });
    },
    cloneFilter: (entityIdFrom, entityIdTo) => {
      set((state) => {
        const filterData = structuredClone(state.filterData);

        filterData[entityIdTo] = structuredClone(filterData[entityIdFrom]);

        return { filterData };
      });
    },
    resetFilterSelection: (
      entityId: string,
      key?: string,
      omitFilterKeys?: string[]
    ) => {
      set((state) => {
        const filterData = structuredClone(state.filterData);

        if (!filterData[entityId]?.visualisedFilter) {
          return state;
        }

        const currentFilters = filterData[entityId].visualisedFilter;

        for (const filterKey of Object.keys(currentFilters)) {
          if (
            (key == null || key === filterKey) &&
            !omitFilterKeys?.includes(filterKey) &&
            currentFilters[filterKey]
          ) {
            currentFilters[filterKey].selection = undefined;
          }
        }

        filterData[entityId].meta = { initialised: true, loaded: false };
        filterData[entityId].selectionForQueryKey = pickSelectionsFromFilter(
          filterData[entityId].visualisedFilter
        );

        return {
          ...state,
          filterData,
        };
      });
    },
    deleteFilter: (entityId: string) => {
      set((state) => {
        const filterData = structuredClone(state.filterData);
        delete filterData[entityId];

        return {
          filterData,
        };
      });
    },
    // This is used to reset all filters to un-loaded state in case of cache invalidation
    setAllFilterUnLoaded: () => {
      set((state) => {
        const filterData = structuredClone(state.filterData);
        for (const entityId of Object.keys(filterData)) {
          if (filterData[entityId]) {
            filterData[entityId].meta = {
              ...filterData[entityId].meta,
              loaded:
                Object.keys(filterData[entityId].visualisedFilter).length > 0
                  ? false
                  : true,
            };
          }
        }
        return { filterData };
      });
    },
    resetFilterSlice: () => {
      set({ ...initialState });
    },
  };
};
